import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Button,
  Checkbox,
  FormControlLabel,
  FormLabel,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { PreLoader } from "../../subcomponents/Preloader";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  fillAutocompleteValues,
  updateData,
} from "../../actions/actionItems";
import InfoIcon from "@mui/icons-material/Info";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DraggableItemList from "../../subcomponents/DNDComponent";
import AlertComponent from "../../subcomponents/AlertComponent";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { taxesTooltip } from "../../../constants/TooltipInformation";
import dayjs, { Dayjs } from "dayjs";
import { RoomPropertyEnum } from "../../../constants/NavbarList";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import { ClearButton } from "../../subcomponents/ClearButton";
import {
  validateString,
  validationLength,
  validationMessages,
  validationType,
} from "../../actions/validations";
import {
  versaFieldErrors,
  versaFieldHelperText,
} from "../../../constants/VersaHelperTexts";
import { removeSampleTestArray } from "../../actions/cleanup";
import { CopyButton } from "../../subcomponents/CopyButton";
import useScrollToTop from "../../hooks/useScrollToTop";
import {
  buildTaxMaintenancesFromFields,
  readTaxMaintenanceFields,
} from "../../../constants/TaxMaintenance";

const normalizePostTypeName = (value: unknown): string | null => {
  if (value == null || value === "") {
    return null;
  }
  const str = String(value);
  if (str.includes(" - ")) {
    return str.split(" - ").slice(1).join(" - ").trim();
  }
  return str;
};

const parseTaxDate = (value: unknown): Dayjs | null => {
  if (value == null || value === "") {
    return null;
  }
  const parsed = dayjs(value as string);
  return parsed.isValid() ? parsed : null;
};

const formatTaxDate = (value: Dayjs | null): string | null =>
  value?.isValid() ? value.format("YYYY-MM-DD") : null;

const readTaxBoolean = (
  record: Record<string, unknown> | null | undefined,
  keys: string[],
  fallback = false,
): boolean => {
  if (!record) return fallback;
  for (const key of keys) {
    const value = record[key];
    if (value === true || value === false) return value;
    if (value === "true") return true;
    if (value === "false") return false;
  }
  return fallback;
};

const Taxes = ({ data, fieldValues }: { data: any; fieldValues: any }) => {
  const [taxType, setTaxType] = useState<string | null>(null);
  const [taxTypeError, setTaxTypeError] = useState<boolean>(false);
  const [postTypeList, setPostTypeList] = useState<string[]>([]);
  const [openAccordion, setOpenAccordion] = useState<boolean>(false);
  const [postType, setPostType] = useState<string | null>(null);
  const [exemptDays, setExemptDays] = useState<number | null>(0);
  const [exempt, setExempt] = useState<string | null>(null);
  const [taxExemptList, setTaxExemptList] = useState<string[]>([]);
  const [extractCode, setExtractCode] = useState<string | null>(null);
  const [active, setActive] = useState<boolean>(true);
  const [taxable, setTaxable] = useState<boolean>(false);
  const [occupancyTax, setOccupancyTax] = useState<boolean>(false);
  const [postWithRoom, setPostWithRoom] = useState<boolean>(false);
  const [occupancyTaxBasedOnRooms, setOccupancyTaxBasedOnRooms] =
    useState<boolean>(false);
  const [specializedTax, setSpecializedTax] = useState<boolean>(false);
  const [taxForFirstFolio, setTaxForFirstFolio] = useState<boolean>(false);
  const [GSTTax, setGSTTax] = useState<boolean>(false);
  const [definedSplits, setDefinedSplits] = useState<boolean>(false);
  const [excludeFlatTax, setExcludeFlatTax] = useState<boolean>(false);
  const [taxRangeFrom, setTaxRangeFrom] = useState<Dayjs | null>(
    dayjs(new Date()),
  );
  const [taxRangeTo, setTaxRangeTo] = useState<Dayjs | null>(null);
  const [taxPercent, setTaxPercent] = useState<number | null>(null);
  const [flatAmount, setFlatAmount] = useState<number | null>(null);
  const [isFlat, setIsFlat] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [alertType, setAlertType] = useState<"success" | "error">("error");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [taxInformation, setTaxInformation] = useState<any>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const { topRef, scrollToTop } = useScrollToTop();

  const taxPayload = {
    taxType: taxType,
    postTypeName: normalizePostTypeName(postType),
    exemptDays: exemptDays,
    effectiveDate: formatTaxDate(taxRangeFrom) ?? dayjs().format("YYYY-MM-DD"),
    taxRangeTo: formatTaxDate(taxRangeTo),
    pSTTax: "",
    extractCode: extractCode,
    isTaxable: taxable,
    isPostWithRoom: postWithRoom,
    isSpecializedUse: specializedTax,
    isGSTTax: GSTTax,
    isOccupancyTax: occupancyTax,
    isOCCTaxNoOfRooms: occupancyTaxBasedOnRooms,
    isPostTaxToFirstFolio: taxForFirstFolio,
    isShowOnSplits: definedSplits,
    taxExemptCategory: exempt,
    taxMaintenances: buildTaxMaintenancesFromFields({
      fromDate: formatTaxDate(taxRangeFrom) ?? dayjs().format("YYYY-MM-DD"),
      toDate: formatTaxDate(taxRangeTo),
      isFlat,
      taxPercent,
      flatAmount,
    }),
    isExcludeFlatTaxOnZeroCharge: excludeFlatTax,
    taxPercent,
    flatAmount,
    isActive: active,
  };

  const addTaxDefinition = () => {
    if (!validateInput()) {
      return;
    }
    if (!postType || postType === null || postType === "") {
      setAlertMessage("Enter the post type");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    if (
      checkDuplicate(
        Array.isArray(taxInformation) ? taxInformation : [],
        DNDFieldsEnum.TAXCODE,
        taxType,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }

    setTaxInformation((prev: any) => {
      return updateData(
        Array.isArray(prev)
          ? [
              ...prev,
              {
                id: prev.length + 1,
                ...taxPayload,
              },
            ]
          : [
              {
                id: 1,
                ...taxPayload,
              },
            ],
      );
    });
    setLastUpdatedRecord(taxPayload);
    editTaxDefinition(null);
    setEditClicked(false);
  };

  const editTaxDefinition = (list: any) => {
    if (!list) {
      setSelectedId(null);
      setEditClicked(false);
      setTaxType(null);
      setPostType(null);
      setExemptDays(0);
      setTaxRangeFrom(dayjs());
      setTaxRangeTo(null);
      setExtractCode(null);
      setTaxable(false);
      setPostWithRoom(false);
      setSpecializedTax(false);
      setGSTTax(false);
      setOccupancyTax(false);
      setOccupancyTaxBasedOnRooms(false);
      setTaxForFirstFolio(false);
      setDefinedSplits(false);
      setExempt(null);
      setExcludeFlatTax(false);
      setTaxPercent(null);
      setFlatAmount(null);
      setIsFlat(false);
      setActive(true);
      return;
    }
    list?.id && setSelectedId(list.id);
    setEditClicked(true);
    setTaxType(list?.taxType);
    setPostType(normalizePostTypeName(list?.postTypeName));
    setExemptDays(list?.exemptDays);
    const maintenanceFields = readTaxMaintenanceFields(list?.taxMaintenances);
    setTaxRangeFrom(
      parseTaxDate(maintenanceFields?.fromDate ?? list?.effectiveDate) ??
        dayjs(),
    );
    setTaxRangeTo(parseTaxDate(maintenanceFields?.toDate ?? list?.taxRangeTo));
    setIsFlat(maintenanceFields?.isFlat ?? false);
    setTaxPercent(maintenanceFields?.taxPercent ?? list?.taxPercent ?? null);
    setFlatAmount(maintenanceFields?.flatAmount ?? list?.flatAmount ?? null);
    setExtractCode(list?.extractCode);
    setTaxable(list ? list.isTaxable : false);
    setPostWithRoom(list ? list.isPostWithRoom : false);
    setSpecializedTax(list ? list.isSpecializedUse : false);
    setGSTTax(list ? list.isGSTTax : false);
    setOccupancyTax(list ? list.isOccupancyTax : false);
    setOccupancyTaxBasedOnRooms(list ? list.isOCCTaxNoOfRooms : false);
    setTaxForFirstFolio(list ? list.isPostTaxToFirstFolio : false);
    setDefinedSplits(list ? list.isShowOnSplits : false);
    setExempt(list?.taxExemptCategory);
    setExcludeFlatTax(
      readTaxBoolean(list, [
        "isExcludeFlatTaxOnZeroCharge",
        "isExcludeFlatTax",
        "excludeFlatTax",
      ]),
    );
    setActive(list ? list.isActive : true);
  };

  const updateTax = () => {
    if (!validateInput()) return;
    if (!postType || postType === null || postType === "") {
      setAlertMessage("Enter the post type");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    if (
      checkDuplicate(
        (Array.isArray(taxInformation) ? taxInformation : []).filter(
          (item: any) => String(item.id) !== String(selectedId),
        ),
        DNDFieldsEnum.TAXCODE,
        taxType,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setTaxInformation((prev: any) =>
      Array.isArray(prev)
        ? prev.map((item: any) => {
            if (String(item.id) === String(selectedId)) {
              return {
                ...item,
                ...taxPayload,
              };
            }
            return item;
          })
        : prev,
    );
    editTaxDefinition(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${RoomPropertyEnum.taxes} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const deleteTaxDefinition = (id: number) => {
    setTaxInformation(deleteItem(taxInformation, id));
  };

  const validateInput = () => {
    const trimmedTaxType = String(taxType ?? "").trim();
    if (!trimmedTaxType) {
      setTaxTypeError(true);
      return false;
    }
    if (
      !validateString(
        trimmedTaxType,
        validationLength.taxType,
        validationType.CODE,
      )
    ) {
      setTaxTypeError(true);
      return false;
    }
    setTaxTypeError(false);
    return true;
  };

  useEffect(() => {
    data(taxInformation);
    editClicked &&
      setSelectedId(checkData(taxInformation, selectedId) ? selectedId : null);
  }, [taxInformation]);

  useEffect(() => {
    const taxTypeMissing = !taxType || taxType === null || taxType === "";
    const postTypeMissing = !postType || postType === null || postType === "";
    setOpenAccordion(taxTypeMissing || postTypeMissing || Boolean(selectedId));
  }, []);

  useEffect(() => {
    if (fieldValues) {
      setTaxInformation(
        Array.isArray(fieldValues[RoomPropertyEnum.taxes])
          ? fieldValues[RoomPropertyEnum.taxes]
          : fieldValues[RoomPropertyEnum.taxes]
            ? [fieldValues[RoomPropertyEnum.taxes]]
            : null,
      );
      setPostTypeList(
        fillAutocompleteValues(
          fieldValues[RoomPropertyEnum.taxPostTypes],
          DNDFieldsEnum.POSTTYPENAME,
          "",
        ),
      );
      setTaxExemptList(
        fillAutocompleteValues(
          fieldValues[RoomPropertyEnum.taxExemptCategory],
          DNDFieldsEnum.TAXEXEMPTCODE,
          DNDFieldsEnum.TAXEXEMPTNAME,
        ),
      );
    }
  }, []);

  const taxTypeHelperText = taxTypeError
    ? !(taxType?.trim() ?? "")
      ? versaFieldErrors.required
      : versaFieldErrors.taxes.taxType
    : versaFieldHelperText.taxes.taxType;

  return (
    <Stack direction="column" gap="10px">
      <div ref={topRef} />
      <Stack direction="column" width="100%">
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "20px",
          }}
        >
          <Stack
            direction="row"
            width="100%"
            justifyContent="space-between"
            alignItems="center"
            gap="10px"
            mb={4}
          >
            <Typography variant="h6" component="div" sx={textStyle}>
              Taxes Information
              <Tooltip title={tooltipComponent(taxesTooltip.header)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Stack direction="row" gap="10px" justifyContent="space-between">
              {editClicked && (
                <ClearButton
                  click={() => {
                    editTaxDefinition(null);
                    setSelectedId(null);
                  }}
                />
              )}
              {lastUpdatedRecord && (
                <CopyButton
                  click={() => {
                    editTaxDefinition(lastUpdatedRecord);
                  }}
                />
              )}
            </Stack>
          </Stack>

          <Accordion
            expanded={openAccordion}
            onChange={() => setOpenAccordion(!openAccordion)}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1-content"
              id="panel1-header"
            >
              <Typography component="span">Basic Tax Details</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack direction="column" width="100%" gap="20px">
                <Stack
                  className="form-two-col-row"
                  direction="row"
                  width="100%"
                  gap="20px"
                >
                  <Stack direction="column" width="100%" gap="5px">
                    <FormLabel component="legend">
                      Tax Type
                      <Tooltip title={tooltipComponent(taxesTooltip.taxType)}>
                        <InfoIcon fontSize="small" />
                      </Tooltip>
                    </FormLabel>
                    <TextField
                      id="taxtype"
                      variant="outlined"
                      placeholder="Tax Type"
                      value={taxType ?? ""}
                      error={taxTypeError}
                      helperText={taxTypeHelperText}
                      sx={{ width: "100%" }}
                      onChange={(e: any) => {
                        setTaxType(e.target.value.toUpperCase() || null);
                        if (taxTypeError) {
                          setTaxTypeError(false);
                        }
                      }}
                    />
                  </Stack>
                  <Stack direction="column" width="100%" gap="5px">
                    <FormLabel component="legend">
                      Extract Code
                      <Tooltip
                        title={tooltipComponent(taxesTooltip.extractCode)}
                      >
                        <InfoIcon fontSize="small" />
                      </Tooltip>
                    </FormLabel>
                    <TextField
                      id="extractCode"
                      variant="outlined"
                      placeholder="Extract Code"
                      type="number"
                      value={extractCode ?? ""}
                      helperText={taxesTooltip.extractCode}
                      sx={{ width: "100%" }}
                      onChange={(e: any) => {
                        setExtractCode(
                          e.target.value >= 0 ? e.target.value : null,
                        );
                      }}
                    />
                  </Stack>
                  <Stack direction="column" width="100%" gap="5px">
                    <FormLabel component="legend">
                      Exempt Days
                      <Tooltip
                        title={tooltipComponent(taxesTooltip.exemptChangeDays)}
                      >
                        <InfoIcon fontSize="small" />
                      </Tooltip>
                    </FormLabel>
                    <TextField
                      id="exemptDays"
                      variant="outlined"
                      placeholder="Exempt Days"
                      type="number"
                      value={exemptDays ?? ""}
                      helperText={"Exempt Days"}
                      sx={{ width: "100%" }}
                      onChange={(e: any) => {
                        setExemptDays(
                          e.target.value >= 0 ? e.target.value : null,
                        );
                      }}
                    />
                  </Stack>
                </Stack>
                <Stack
                  className="form-two-col-row"
                  direction="row"
                  gap="20px"
                  width="100%"
                >
                  <Stack direction="column" width="100%" gap="5px">
                    <FormLabel component="legend">
                      Post Type
                      <Tooltip title={tooltipComponent(taxesTooltip.postType)}>
                        <InfoIcon fontSize="small" />
                      </Tooltip>
                    </FormLabel>
                    <Autocomplete
                      id="postType"
                      disablePortal
                      options={removeSampleTestArray(postTypeList)}
                      sx={{ width: "100%" }}
                      disabled={!postTypeList}
                      value={postType ?? ""}
                      onChange={(_, newValue) => setPostType(newValue)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Post Type"
                          helperText={versaFieldHelperText.taxes.postType}
                        />
                      )}
                    />
                  </Stack>
                  <Stack direction="column" width="100%" gap="5px">
                    <FormLabel component="legend">
                      Tax Exempt Categories
                      <Tooltip
                        title={tooltipComponent(taxesTooltip.exemptChangeDays)}
                      >
                        <InfoIcon fontSize="small" />
                      </Tooltip>
                    </FormLabel>
                    <Autocomplete
                      id="taxExemptCategory"
                      disablePortal
                      options={taxExemptList}
                      sx={{ width: "100%" }}
                      disabled={!taxExemptList}
                      value={exempt ?? ""}
                      onChange={(e: any) => setExempt(e.target.innerText)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Tax Exempt Categories"
                        />
                      )}
                    />
                  </Stack>
                </Stack>
              </Stack>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel-range-content"
              id="panel-range-header"
            >
              <Typography component="span">Tax Range &amp; Amount</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack direction="column" width="100%" gap="20px">
                <Stack direction="row" width="100%" gap="20px">
                  <Stack direction="column" width="100%" gap="5px">
                    <FormLabel component="legend">
                      Range From
                      <Tooltip
                        title={tooltipComponent(taxesTooltip.effectiveDays)}
                      >
                        <InfoIcon fontSize="small" />
                      </Tooltip>
                    </FormLabel>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        value={taxRangeFrom}
                        sx={{ width: "100%" }}
                        onChange={(value) => {
                          setTaxRangeFrom(value);
                          if (
                            value &&
                            taxRangeTo &&
                            taxRangeTo.isBefore(value, "day")
                          ) {
                            setTaxRangeTo(null);
                          }
                        }}
                      />
                    </LocalizationProvider>
                  </Stack>
                  <Stack direction="column" width="100%" gap="5px">
                    <FormLabel component="legend">Range To</FormLabel>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        value={taxRangeTo}
                        minDate={taxRangeFrom ?? undefined}
                        sx={{ width: "100%" }}
                        onChange={(value) => {
                          if (!value) {
                            setTaxRangeTo(null);
                            return;
                          }
                          if (
                            taxRangeFrom &&
                            value.isBefore(taxRangeFrom, "day")
                          ) {
                            setAlertMessage(
                              validationMessages.endDateBeforeStart,
                            );
                            setAlertType("error");
                            setOpenAlert(true);
                            return;
                          }
                          setTaxRangeTo(value);
                        }}
                      />
                    </LocalizationProvider>
                  </Stack>
                </Stack>
                <Stack direction="row" gap="20px" width="100%">
                  <Stack
                    direction="row"
                    gap="10px"
                    width="50%"
                    alignItems="center"
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          id="isFlat"
                          checked={isFlat}
                          onChange={(e: any) => {
                            setIsFlat(e.target.checked);
                            setTaxPercent(null);
                            setFlatAmount(null);
                          }}
                        />
                      }
                      label="Amount"
                      sx={{ whiteSpace: "nowrap", minWidth: "fit-content" }}
                    />
                    <Stack direction="column" width="100%" gap="5px">
                      <FormLabel component="legend">
                        {isFlat ? "Flat Amount" : "Tax Percentage"}
                      </FormLabel>
                      <TextField
                        id="amountField"
                        variant="outlined"
                        placeholder={isFlat ? "Flat Amount" : "Tax Percentage"}
                        type="number"
                        helperText={""}
                        value={(isFlat ? flatAmount : taxPercent) ?? ""}
                        sx={{ width: "100%" }}
                        onChange={(e: any) => {
                          const val =
                            e.target.value >= 0 ? e.target.value : null;
                          if (isFlat) setFlatAmount(val);
                          else setTaxPercent(val);
                        }}
                      />
                    </Stack>
                  </Stack>
                </Stack>
              </Stack>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1-content"
              id="panel1-header"
            >
              <Typography component="span">Additional Tax Details</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack direction="column" width="100%" gap="20px">
                <Stack
                  className="form-checkbox-row form-two-col-row"
                  direction="row"
                  gap="20px"
                  flexWrap="wrap"
                  p={1}
                >
                  <Stack
                    direction="row"
                    width="max-content"
                    height="max-content"
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          id="taxable"
                          checked={taxable}
                          onChange={(e: any) => setTaxable(e.target.checked)}
                        />
                      }
                      label="Taxable ?"
                    />
                    <Tooltip title={tooltipComponent(taxesTooltip.taxable)}>
                      <InfoIcon fontSize="small" />
                    </Tooltip>
                  </Stack>
                  <Stack
                    direction="row"
                    width="max-content"
                    height="max-content"
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          id="occupancyTax"
                          checked={occupancyTax}
                          onChange={(e: any) =>
                            setOccupancyTax(e.target.checked)
                          }
                        />
                      }
                      label="Occupancy Tax ?"
                    />
                    <Tooltip title={""}>
                      <InfoIcon fontSize="small" />
                    </Tooltip>
                  </Stack>
                  <Stack
                    direction="row"
                    width="max-content"
                    height="max-content"
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          id="postWithRoom"
                          checked={postWithRoom}
                          onChange={(e: any) =>
                            setPostWithRoom(e.target.checked)
                          }
                        />
                      }
                      label="Post with room ?"
                    />
                    <Tooltip title={""}>
                      <InfoIcon fontSize="small" />
                    </Tooltip>
                  </Stack>
                  <Stack
                    direction="row"
                    width="max-content"
                    height="max-content"
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          id="occupancyTaxBasedOnRooms"
                          checked={occupancyTaxBasedOnRooms}
                          onChange={(e: any) =>
                            setOccupancyTaxBasedOnRooms(e.target.checked)
                          }
                        />
                      }
                      label="Occupancy Tax based Number on rooms?"
                    />
                    <Tooltip title={""}>
                      <InfoIcon fontSize="small" />
                    </Tooltip>
                  </Stack>
                  <Stack
                    direction="row"
                    width="max-content"
                    height="max-content"
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          id="SpecializedTax"
                          checked={specializedTax}
                          onChange={(e: any) =>
                            setSpecializedTax(e.target.checked)
                          }
                        />
                      }
                      label="Specialized Use?"
                    />
                    <Tooltip title={""}>
                      <InfoIcon fontSize="small" />
                    </Tooltip>
                  </Stack>
                  <Stack
                    direction="row"
                    width="max-content"
                    height="max-content"
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          id="PostThisTaxToFirstFolioInRooms"
                          checked={taxForFirstFolio}
                          onChange={(e: any) =>
                            setTaxForFirstFolio(e.target.checked)
                          }
                        />
                      }
                      label="Post this tax to first folio in Rooms ?"
                    />
                    <Tooltip title={""}>
                      <InfoIcon fontSize="small" />
                    </Tooltip>
                  </Stack>
                  <Stack
                    direction="row"
                    width="max-content"
                    height="max-content"
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          id="GstTax"
                          checked={GSTTax}
                          onChange={(e: any) => setGSTTax(e.target.checked)}
                        />
                      }
                      label="GST Tax ?"
                    />
                    <Tooltip title={""}>
                      <InfoIcon fontSize="small" />
                    </Tooltip>
                  </Stack>
                  <Stack
                    direction="row"
                    width="max-content"
                    height="max-content"
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          id="ShowOnDefinedSplits"
                          checked={definedSplits}
                          onChange={(e: any) =>
                            setDefinedSplits(e.target.checked)
                          }
                        />
                      }
                      label="Show on Defined Splits ?"
                    />
                    <Tooltip title={""}>
                      <InfoIcon fontSize="small" />
                    </Tooltip>
                  </Stack>
                  <Stack
                    direction="row"
                    width="max-content"
                    height="max-content"
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          id="ExcludeFlatTaxOnZeroCharge"
                          checked={excludeFlatTax}
                          onChange={(e: any) =>
                            setExcludeFlatTax(e.target.checked)
                          }
                        />
                      }
                      label="Exclude flat tax on zero charge ?"
                    />
                    <Tooltip title={""}>
                      <InfoIcon fontSize="small" />
                    </Tooltip>
                  </Stack>
                </Stack>
              </Stack>
            </AccordionDetails>
          </Accordion>

          <Stack direction="row" width="100%" gap="2px">
            <FormControlLabel
              control={
                <Switch checked={active} onChange={() => setActive(!active)} />
              }
              label="Active?"
            />
            <Tooltip title={tooltipComponent(taxesTooltip.active)}>
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Stack>

          <Stack direction="row" width="100%" justifyContent="flex-end">
            <Button
              variant="contained"
              sx={{
                cursor: "pointer",
                border: "1px solid gray",
                borderRadius: "10px",
                background: "darkgreen",
                color: "white",
              }}
              onClick={selectedId ? updateTax : addTaxDefinition}
              id="addNewTaxInformation"
            >
              {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Tax
              Information
            </Button>
          </Stack>

          <Stack direction="column" gap="10px" mt={4}>
            <Typography variant="h6">Tax Information Available</Typography>
            <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
              Do not use sample data. Those are for demo purposes only!
            </Typography>
            <Stack direction="column" gap="10px">
              {taxInformation ? (
                <DraggableItemList
                  itemData={taxInformation}
                  onOrderUpdate={(order) => setTaxInformation(order)}
                  onEdit={(info) => {
                    editTaxDefinition(info);
                    scrollToTop();
                  }}
                  onDelete={(id) => deleteTaxDefinition(id)}
                  code={DNDFieldsEnum.TAXCODE}
                  name={DNDFieldsEnum.TAXNAME}
                />
              ) : (
                <Typography variant="body2">
                  No Tax Information Information Available
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Stack>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default Taxes;
